import { useCallback, useMemo } from "react";

import { useToast } from "shared/hooks";

import { useSystemApiService } from "shared/services";

import { useSystemStore } from "../contexts";
import type { SendSupportRequestParamsModel } from "../types/BootsrapModel";
import type { Currencies } from "../types/Currency";

const useSystem = () => {
	const systemService = useSystemApiService();

	const store = useSystemStore();
	const { setState, setInitial } = useSystemStore();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			async getBootstrap(params: { refresh?: boolean; slim?: boolean }) {
				try {
					setState({ loading: true });
					const res = await systemService.getBootstrap(params);
					if (!!res) {
						setState({
							bootstrap: res
						});
					}
					return null;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get bootstrap config", noIcon: true });
					return null;
				} finally {
					setState({ loading: false });
				}
			},
			async sendRequestToSupport(params: SendSupportRequestParamsModel) {
				try {
					setState({ loading: true });
					await systemService.sendRequestToSupport(params);
					showToast({ text: "Your request to support successfully sent", noIcon: true });
					return true;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't send support request", noIcon: true });
					return false;
				} finally {
					setState({ loading: false });
				}
			},
			async getCountryList(order?: "asc" | "desc") {
				try {
					setState({ loading: true });
					const res = await systemService.getCountryList(order);
					setState({ countries: res.data.countries });
					return res.data.countries;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of countries", noIcon: true });
					return [];
				} finally {
					setState({ loading: false });
				}
			},
			async getCurrencyList() {
				try {
					setState({ loading: true });
					const res = await systemService.getCurrencyList();
					const currencies = Array.isArray(res.data) ? res.data : Object.values(res.data);
					setState({ currencies });
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of currencies", noIcon: true });
					return [];
				} finally {
					setState({ loading: false });
				}
			},
			async getPaymentSystems(params?: { version?: string; currency?: Currencies }) {
				try {
					setState({ loading: true });
					const res = await systemService.getPaymentSystems(params);
					setState({ paymentSystems: res.data });
					return res.data;
				} catch (error) {
					console.log(error);
					showToast({ text: "Can't get list of payment systems", noIcon: true });
					return [];
				} finally {
					setState({ loading: false });
				}
			},
			setAuthDialog(authDialog?: { open: boolean; flow?: "bonus" | "login" | "signUp"; next?: null | string }) {
				setState({ authDialog });
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, systemService, showToast, setInitial]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useSystem;
